/**
 * Página PÚBLICA de validação de holerite via QR code.
 * Rota: /validar-holerite/:hash  — não exige login.
 */

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { CheckCircle2, XCircle, Shield, Loader2, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtBRL = (v) => v === null || v === undefined ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDateTime = (s) => s ? new Date(s).toLocaleString('pt-BR') : '—';

const ValidarHoleritePage = () => {
  const { hash } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/api/calculos-dp/holerites/validar/${hash}`)
      .then(r => { if (active) setData(r.data); })
      .catch(() => { if (active) setData({ valido: false, mensagem: 'Erro ao consultar' }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [hash]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center p-6" data-testid="validar-holerite-page">
      <Card className="max-w-2xl w-full shadow-2xl">
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2 text-xl">
            <Shield className="h-6 w-6 text-indigo-600" />
            Validação de Holerite
          </CardTitle>
          <CardDescription>GridProfile — Sistema de comprovação eletrônica</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-500"><Loader2 className="h-6 w-6 animate-spin mr-2" />Verificando...</div>
          ) : data?.valido ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-300 rounded-lg">
                <CheckCircle2 className="h-8 w-8 text-emerald-600 shrink-0" />
                <div>
                  <div className="font-bold text-emerald-800">Holerite autêntico</div>
                  <div className="text-xs text-emerald-700">Documento validado pelo banco de dados GridProfile</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="col-span-2">
                  <div className="text-xs text-slate-500 uppercase">Colaborador</div>
                  <div className="font-semibold text-lg">{data.holerite.colaborador}</div>
                  <div className="text-xs text-slate-600">{data.holerite.cargo}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase">Competência</div>
                  <div className="font-medium">{data.holerite.competencia}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase">Tipo</div>
                  <div><Badge variant="outline">{data.holerite.tipo}</Badge></div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase">Total Proventos</div>
                  <div className="font-medium text-emerald-700">{fmtBRL(data.holerite.total_proventos)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase">Total Descontos</div>
                  <div className="font-medium text-red-700">{fmtBRL(data.holerite.total_descontos)}</div>
                </div>
                <div className="col-span-2 p-3 bg-indigo-50 border border-indigo-300 rounded">
                  <div className="text-xs text-indigo-700 uppercase">LÍQUIDO RECEBIDO</div>
                  <div className="text-3xl font-bold text-indigo-700">{fmtBRL(data.holerite.total_liquido)}</div>
                </div>
                <div className="col-span-2 text-xs text-slate-500 border-t pt-3 mt-2 space-y-1">
                  <div>Documento gerado em: <b>{fmtDateTime(data.holerite.gerado_em)}</b></div>
                  <div>Assinatura do colaborador: {data.holerite.assinado_pelo_colaborador ? (
                    <span className="text-emerald-700 font-medium">✓ Assinado eletronicamente</span>
                  ) : (
                    <span className="text-amber-700">Pendente</span>
                  )}</div>
                  <div className="font-mono text-[10px] mt-2 break-all">Hash: {hash}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-6 bg-red-50 border border-red-300 rounded-lg">
              <XCircle className="h-10 w-10 text-red-600 shrink-0" />
              <div>
                <div className="font-bold text-red-800 text-lg">Holerite inválido ou não encontrado</div>
                <div className="text-sm text-red-700 mt-1">{data?.mensagem || 'O hash informado não corresponde a nenhum documento emitido pelo GridProfile.'}</div>
              </div>
            </div>
          )}
          <div className="mt-6 pt-4 border-t flex items-center justify-between text-xs text-slate-500">
            <Link to="/" className="hover:underline flex items-center gap-1"><FileText className="h-3 w-3" />Acesso ao GridProfile</Link>
            <span>v1.0 · CLT/eSocial</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ValidarHoleritePage;
